import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../AuthProvider";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Autocomplete,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Link,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Divider,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

import { catalogTokens, parseApiError } from "./VendorCatalogTheme";
import useCategoryTaxonomy from "./useCategoryTaxonomy";

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: `${catalogTokens.radius.md}px` },
};

// mode="upload" (default): the "Upload Catalog" tab, creates a brand-new catalog.
let entrySeq = 0;
const nextEntryKey = () => `entry-${++entrySeq}-${Date.now()}`;

const emptyEntry = () => ({
  key: nextEntryKey(),
  categoryMode: "single", // "single" | "multiple"
  selectedCategories: [],
  selectedBrands: [],
  selectedSubcategories: [],
  selectedCollections: [],
  minPrice: "",
  maxPrice: "",
  file: null,
  priceListMode: null, // "inline" (baked into the catalog PDF) | "separate" — no default, user must pick
  priceListFile: null,
  remarks: "",
});

const entryFromInitialValues = (initialValues) => ({
  key: nextEntryKey(),
  categoryMode: (initialValues?.categories?.length || 0) > 1 ? "multiple" : "single",
  selectedCategories: initialValues?.categories || [],
  selectedBrands: initialValues?.brands || [],
  selectedSubcategories: initialValues?.subCategories || [],
  selectedCollections: initialValues?.collections || [],
  minPrice: initialValues?.minPrice != null ? String(initialValues.minPrice) : "",
  maxPrice: initialValues?.maxPrice != null ? String(initialValues.maxPrice) : "",
  file: null,
  priceListMode: initialValues?.hasSeparatePriceList ? "separate" : "inline",
  priceListFile: null,
  remarks: initialValues?.remarks || "",
});

// One PDF drag/drop dropzone, reused for the catalog file and the optional separate price-list
// file. Keeps its own drag-hover state, so each dropzone on the page is independent. 
// for reuse by VendorCatalogReplacePriceListDialog.
export const PdfDropzone = ({ file, onSelect, onRemove, label, maxSizeLabel = "100MB" }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  return (
    <Box>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        hidden
        onChange={(e) => { onSelect(e.target.files?.[0]); if (inputRef.current) inputRef.current.value = ""; }}
      />
      {file ? (
        <Box
          sx={{
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
            p: 2,
            borderRadius: `${catalogTokens.radius.lg}px`,
            border: `1px solid ${catalogTokens.color.border}`,
            bgcolor: catalogTokens.color.surfaceMuted,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
            <PictureAsPdfIcon sx={{ color: "#dc2626" }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{file.name}</Typography>
              <Typography variant="caption" sx={{ color: catalogTokens.color.textSecondary }}>
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </Typography>
            </Box>
          </Box>
          <Chip
            size="small"
            icon={<CloseIcon sx={{ fontSize: 14 }} />}
            label="Remove"
            onClick={onRemove}
            sx={{ cursor: "pointer" }}
          />
        </Box>
      ) : (
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            onSelect(e.dataTransfer.files?.[0]);
          }}
          onClick={() => inputRef.current?.click()}
          sx={{
            cursor: "pointer",
            border: "2px dashed",
            borderColor: dragOver ? catalogTokens.color.primary : catalogTokens.color.border,
            borderRadius: `${catalogTokens.radius.lg}px`,
            p: 3,
            textAlign: "center",
            bgcolor: dragOver ? catalogTokens.color.primarySoft : catalogTokens.color.surfaceMuted,
            transition: "all 0.15s ease",
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 30, color: catalogTokens.color.primary, mb: 0.5 }} />
          <Typography variant="body2" fontWeight={600} sx={{ color: catalogTokens.color.textPrimary }}>
            {label || "Drag & drop the PDF here"}
          </Typography>
          <Typography variant="caption" sx={{ color: catalogTokens.color.textSecondary }}>
            or click to browse · PDF up to {maxSizeLabel}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// One catalog's worth of fields: brands / category(ies) / sub-categories / collections /
// price range / catalog PDF / price-list toggle (+ its own PDF when separate). Rendered once per
// entry in `entries` — several times in upload mode's batch flow, exactly once in replace mode.
const CatalogEntryBlock = ({
  entry, index, total, onChange, onRemove,
  brands, categories, subcategoryOptions, collections, collectionGroupLabel,
  errorMessage,
}) => {
  const set = (patch) => onChange(entry.key, patch);
  const validateAndSetFile = (field, maxSizeMb, selected) => {
    if (!selected) return;
    const isPdf = selected.type === "application/pdf" || selected.name?.toLowerCase().endsWith(".pdf");
    if (!isPdf) return set({ [`${field}Error`]: "Only PDF files are allowed" });
    if (selected.size > maxSizeMb * 1024 * 1024) return set({ [`${field}Error`]: `File exceeds ${maxSizeMb}MB limit` });
    set({ [field]: selected, [`${field}Error`]: null });
  };

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: `${catalogTokens.radius.lg}px`,
        border: `1px solid ${catalogTokens.color.border}`,
        bgcolor: catalogTokens.color.surfaceMuted,
      }}
    >
      {total > 1 && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ color: catalogTokens.color.textPrimary }}>
            Catalog {index + 1}
          </Typography>
          {onRemove && (
            <IconButton size="small" onClick={() => onRemove(entry.key)} sx={{ color: catalogTokens.color.textSecondary }}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: `${catalogTokens.radius.md}px` }}>{errorMessage}</Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={brands}
            getOptionLabel={(option) => option.name || ""}
            isOptionEqualToValue={(o, v) => o?.name === v?.name}
            value={entry.selectedBrands}
            onChange={(_, newVal) => set({ selectedBrands: newVal })}
            renderTags={(value, getTagProps) =>
              value.map((option, i) => (
                <Chip
                  {...getTagProps({ index: i })}
                  key={option.name}
                  label={option.name}
                  size="small"
                  sx={{ bgcolor: catalogTokens.color.primarySoft, color: catalogTokens.color.primary, fontWeight: 600 }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Brands" placeholder="Select one or more brands (optional)" sx={fieldSx} />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75 }}>
            Does this catalog cover one category or several?
          </Typography>
          <ToggleButtonGroup
            value={entry.categoryMode}
            exclusive
            size="small"
            onChange={(_, value) => {
              if (!value) return;
              set({ categoryMode: value, selectedCategories: [] });
            }}
            sx={{
              mb: 1.5,
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                borderRadius: `${catalogTokens.radius.md}px !important`,
                border: `1px solid ${catalogTokens.color.border} !important`,
                "&.Mui-selected": { bgcolor: catalogTokens.color.primarySoft, color: catalogTokens.color.primary },
              },
            }}
          >
            <ToggleButton value="single">Single Category</ToggleButton>
            <ToggleButton value="multiple">Multiple Categories</ToggleButton>
          </ToggleButtonGroup>

          {entry.categoryMode === "single" ? (
            <FormControl fullWidth required sx={fieldSx}>
              <InputLabel>Category</InputLabel>
              <Select
                value={entry.selectedCategories[0]?.id || ""}
                label="Category"
                onChange={(e) => {
                  const cat = categories.find((c) => String(c.id) === String(e.target.value));
                  set({ selectedCategories: cat ? [cat] : [] });
                }}
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Autocomplete
              multiple
              options={categories}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(o, v) => o?.id != null && v?.id != null && String(o.id) === String(v.id)}
              value={entry.selectedCategories}
              onChange={(_, newVal) => set({ selectedCategories: newVal })}
              renderTags={(value, getTagProps) =>
                value.map((option, i) => (
                  <Chip
                    {...getTagProps({ index: i })}
                    key={option.id}
                    label={option.name}
                    size="small"
                    sx={{ bgcolor: catalogTokens.color.primarySoft, color: catalogTokens.color.primary, fontWeight: 600 }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Categories" required placeholder="Select all categories covered" sx={fieldSx} />
              )}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={subcategoryOptions}
            getOptionLabel={(option) => option.name || ""}
            groupBy={(option) => option.categoryName}
            isOptionEqualToValue={(o, v) => o?.categoryName === v?.categoryName && o?.name === v?.name}
            value={entry.selectedSubcategories}
            onChange={(_, newVal) => set({ selectedSubcategories: newVal })}
            renderTags={(value, getTagProps) =>
              value.map((option, i) => (
                <Chip
                  {...getTagProps({ index: i })}
                  key={`${option.categoryName}::${option.name}`}
                  label={option.name}
                  size="small"
                  sx={{ bgcolor: catalogTokens.color.surfaceMuted, color: catalogTokens.color.textPrimary, fontWeight: 600 }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Sub-Categories"
                placeholder={entry.selectedCategories.length ? "Select sub-categories (optional)" : "Pick a category first"}
                sx={fieldSx}
              />
            )}
            disabled={entry.selectedCategories.length === 0}
          />
        </Grid>

        <Grid item xs={12}>
          <Autocomplete
            multiple
            options={collections}
            getOptionLabel={(option) => option.name || ""}
            groupBy={(option) => collectionGroupLabel(option.group)}
            isOptionEqualToValue={(o, v) => o?.id != null && v?.id != null && String(o.id) === String(v.id)}
            value={entry.selectedCollections}
            onChange={(_, newVal) => set({ selectedCollections: newVal })}
            renderTags={(value, getTagProps) =>
              value.map((option, i) => (
                <Chip
                  {...getTagProps({ index: i })}
                  key={option.id}
                  label={option.name}
                  size="small"
                  sx={{ bgcolor: catalogTokens.color.surfaceMuted, color: catalogTokens.color.textPrimary, fontWeight: 600 }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label="Collections / Themes" placeholder="Occasion or Celebration tags (optional)" sx={fieldSx} />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth required type="number" label="Min Price" sx={fieldSx}
            value={entry.minPrice} onChange={(e) => set({ minPrice: e.target.value })}
            InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth required type="number" label="Max Price" sx={fieldSx}
            value={entry.maxPrice} onChange={(e) => set({ maxPrice: e.target.value })}
            InputProps={{ startAdornment: <InputAdornment position="start"><CurrencyRupeeIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
          />
        </Grid>

        <Grid item xs={12}>
          <PdfDropzone
            file={entry.file}
            onSelect={(f) => validateAndSetFile("file", 100, f)}
            onRemove={() => set({ file: null })}
            label="Drag & drop the catalog PDF here"
          />
          {entry.fileError && (
            <Typography variant="caption" sx={{ color: "#dc2626", display: "block", mt: 0.5 }}>{entry.fileError}</Typography>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 0.5 }} />
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.75, display: "flex", alignItems: "center", gap: 0.75 }}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 18, color: catalogTokens.color.textSecondary }} />
            Where's the price list? *
          </Typography>
          <ToggleButtonGroup
            value={entry.priceListMode}
            exclusive
            size="small"
            onChange={(_, value) => {
              if (!value) return;
              set({
                priceListMode: value,
                ...(value !== "separate" && {
                  priceListFile: null,
                  priceListFileError: null,
                }),
              });
            }}
            sx={{
              mb: entry.priceListMode === "separate" ? 1.5 : 0.5,
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                borderRadius: `${catalogTokens.radius.md}px !important`,
                border: `1px solid ${catalogTokens.color.border} !important`,
                "&.Mui-selected": { bgcolor: catalogTokens.color.primarySoft, color: catalogTokens.color.primary },
              },
            }}
          >
            <ToggleButton value="inline">Inside this PDF</ToggleButton>
            <ToggleButton value="separate">Separate PDF</ToggleButton>
            <ToggleButton value="na">N/A</ToggleButton>
          </ToggleButtonGroup>

          {!entry.priceListMode && (
            <Typography variant="caption" sx={{ color: catalogTokens.color.textSecondary, display: "block", mb: 0.5 }}>
              Choose one before submitting
            </Typography>
          )}

          {entry.priceListMode === "separate" && (
            <>
              <PdfDropzone
                file={entry.priceListFile}
                onSelect={(f) => validateAndSetFile("priceListFile", 20, f)}
                onRemove={() => set({ priceListFile: null })}
                label="Drag & drop the price list PDF here"
                maxSizeLabel="20MB"
              />
              {entry.priceListFileError && (
                <Typography variant="caption" sx={{ color: "#dc2626", display: "block", mt: 0.5 }}>{entry.priceListFileError}</Typography>
              )}
            </>
          )}
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            required={entry.priceListMode === "na"}
            label={
              entry.priceListMode === "na"
                ? "Remarks, Discounts, or Special Notes"
                : "Remarks, Discounts, or Special Notes (optional)"
            }
            placeholder={
              entry.priceListMode === "na"
                ? "Please mention why the price list is not available..."
                : "Optional — e.g. vendor is offering a flat 20% off across this catalog"
            }
            value={entry.remarks}
            onChange={(e) => set({ remarks: e.target.value })}
            error={
              entry.priceListMode === "na" &&
              !entry.remarks?.trim()
            }
            helperText={
              entry.priceListMode === "na"
                ? "Please provide a reason or relevant details since no price list is available."
                : ""
            }
            sx={fieldSx}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

// mode="replace": used by VendorCatalogReplaceDialog to swap an existing catalog's PDF for a
// newer one from the same vendor — the vendor is fixed (not re-selectable), fields prefill from
// `initialValues`, and submit posts to /{catalogId}/replace instead of creating a fresh upload.
const VendorCatalogUpload = ({ mode = "upload", catalogId, initialValues, onSuccess }) => {
  const isReplaceMode = mode === "replace";
  const { instance } = useMsal();
  const { account } = useAuth();
  const fileInputRef = useRef(null);
  const { brands, categories, subcategoriesByCategory, ensureSubcategoriesFor, collections } = useCategoryTaxonomy();

  const [vendors, setVendors] = useState([]);

  const [vendor, setVendor] = useState(initialValues?.vendor || null);
  const [entries, setEntries] = useState(() => [isReplaceMode ? entryFromInitialValues(initialValues) : emptyEntry()]);

  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const sendAuthenticatedRequest = useCallback(
    async (url, method, body, isFormData = false) => {
      const token = await getApiToken(instance, account);
      const headers = { Authorization: `Bearer ${token}` };
      if (!isFormData) headers["Content-Type"] = "application/json";
      const res = await fetch(url, {
        method,
        headers,
        body: isFormData ? body : body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      const text = await res.text();
      try {
        return text ? JSON.parse(text) : {};
      } catch {
        return text;
      }
    },
    [instance, account]
  );

  useEffect(() => {
    if (!account || isReplaceMode) return; // replace keeps the existing vendor, no picker needed
    sendAuthenticatedRequest(`${urlconfig.backend_api_base_url}/api/v2/vendors`, "GET")
      .then((data) => setVendors(data || []))
      .catch(() => setVendors([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isReplaceMode]);

  // Ensure that the subcategory options are always in sync with the selected categories.
  const allCategoryNamesKey = entries.map((e) => e.selectedCategories.map((c) => c.name).sort().join(",")).join("|");
  useEffect(() => {
    const allCategoryNames = [...new Set(entries.flatMap((e) => e.selectedCategories.map((c) => c.name)))];
    ensureSubcategoriesFor(allCategoryNames);
    setEntries((prev) =>
      prev.map((e) => {
        const selectedNames = new Set(e.selectedCategories.map((c) => c.name));
        const pruned = e.selectedSubcategories.filter((sc) => selectedNames.has(sc.categoryName));
        return pruned.length === e.selectedSubcategories.length ? e : { ...e, selectedSubcategories: pruned };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCategoryNamesKey]);

 
  const collectionGroupLabel = (group) => (group === "OCCASION" ? "Occasion" : "Celebration");
  const subcategoryOptions = Object.values(subcategoriesByCategory).flat();
  const subcategoryOptionsFor = () => subcategoryOptions;

  const updateEntry = (key, patch) => {
    setEntries((prev) => prev.map((e) => (e.key === key ? { ...e, ...patch } : e)));
  };

  const addEntry = () => {
    setEntries((prev) => [...prev, emptyEntry()]);
  };

  const removeEntry = (key) => {
    setEntries((prev) => (prev.length > 1 ? prev.filter((e) => e.key !== key) : prev));
  };

  const buildFormData = (entry) => {
    const formData = new FormData();
    if (!isReplaceMode) formData.append("vendorId", vendor.id);
    entry.selectedCategories.forEach((c) => formData.append("categoryNames", c.name));
    entry.selectedBrands.forEach((b) => formData.append("brandNames", b.name));
    formData.append(
      "subCategoriesJson",
      JSON.stringify(entry.selectedSubcategories.map((sc) => ({ category: sc.categoryName, name: sc.name })))
    );
    entry.selectedCollections.forEach((c) => formData.append("collectionIds", c.id));
    formData.append("minPrice", entry.minPrice);
    formData.append("maxPrice", entry.maxPrice);
    formData.append("file", entry.file);
    if (entry.priceListMode === "separate" && entry.priceListFile) {
      formData.append("priceListFile", entry.priceListFile);
    }
    if (entry.remarks && entry.remarks.trim()) {
      formData.append("remarks", entry.remarks.trim());
    }
    return formData;
  };

  // Single source of truth for "is this entry complete" — drives both the disabled submit
  // button (isEntryValid) and the per-entry error messages shown after a submit attempt
  // (validateEntries), so the two never drift apart.
  const isEntryValid = (e) =>
    e.selectedCategories.length > 0 &&
    e.minPrice !== "" &&
    e.maxPrice !== "" &&
    Number(e.minPrice) <= Number(e.maxPrice) &&
    !!e.file &&
    !!e.priceListMode &&
    !(e.priceListMode === "separate" && !e.priceListFile);

  const validateEntries = () => {
    const errors = {};
    entries.forEach((e) => {
      if (e.selectedCategories.length === 0) errors[e.key] = "Select at least one category";
      else if (e.minPrice === "" || e.maxPrice === "") errors[e.key] = "Enter both min and max price";
      else if (Number(e.minPrice) > Number(e.maxPrice)) errors[e.key] = "Min price cannot be greater than max price";
      else if (!e.file) errors[e.key] = "Attach the catalog PDF";
      else if (!e.priceListMode) errors[e.key] = "Select where the price list is";
      else if (e.priceListMode === "separate" && !e.priceListFile) {
        errors[e.key] = 'Attach the separate price list PDF, or switch back to "Inside this PDF"';
      }
    });
    return errors;
  };

  const canSubmit = (isReplaceMode || !!vendor?.id) && entries.every(isEntryValid);

  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);

    if (!isReplaceMode && !vendor?.id) return setError("Please select a registered vendor");

    const validationErrors = validateEntries();
    if (Object.keys(validationErrors).length > 0) {
      setEntries((prev) => prev.map((e) => ({ ...e, submitError: validationErrors[e.key] || null })));
      setError(entries.length > 1 ? "Fix the highlighted catalog(s) below." : null);
      return;
    }

    setUploading(true);
    const failed = [];
    let successCount = 0;
    let lastSaved = null;

    // Loop through each catalog entry and to submit it to the backend. 
    // If any entry fails, we collect it in the `failed` array to show the user.
    for (const entry of entries) {
      try {
        const url = isReplaceMode
          ? `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/${catalogId}/replace`
          : `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs`;
        const saved = await sendAuthenticatedRequest(url, "POST", buildFormData(entry), true);
        successCount += 1;
        lastSaved = saved;
      } catch (err) {
        failed.push({ ...entry, submitError: err.message || "Upload failed" });
      }
    }
    setUploading(false);

    if (isReplaceMode) {
      if (successCount === 1) {
        onSuccess?.(lastSaved);
      } else {
        setEntries(failed);
      }
      return;
    }

    if (failed.length === 0) {
      setSuccess(`${successCount} ${successCount === 1 ? "catalog" : "catalogs"} uploaded for ${vendor.legalVendorName}.`);
      setEntries([emptyEntry()]);
      onSuccess?.(lastSaved);
    } else if (successCount > 0) {
      setEntries(failed);
      setSuccess(`${successCount} of ${entries.length} catalogs uploaded. Fix the remaining ${failed.length} below and try again.`);
      onSuccess?.(lastSaved);
    } else {
      setEntries(failed);
      setError("Upload failed for the catalog(s) below.");
    }
  };

  return (
    <Box sx={{ maxWidth: 640, mx: "auto" }}>
      <Typography variant="subtitle1" fontWeight={800} sx={{ color: catalogTokens.color.textPrimary, mb: 0.25 }}>
        {isReplaceMode ? "Replace catalog" : "Upload a new catalog"}
      </Typography>
      <Typography variant="body2" sx={{ color: catalogTokens.color.textSecondary, mb: 2.5 }}>
        {isReplaceMode
          ? "Attach the vendor's updated PDF — the old catalog will be removed once this one is saved."
          : "Pick the registered vendor, then tag each catalog's categories/brands/sub-categories/collections/price range and attach its PDF. Add another block below if the vendor sent several catalogs at once."}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: `${catalogTokens.radius.md}px` }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: `${catalogTokens.radius.md}px` }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Box sx={{ mb: 2.5 }}>
        {isReplaceMode ? (
          <TextField
            fullWidth
            label="Vendor"
            value={vendor?.legalVendorName || ""}
            disabled
            sx={fieldSx}
          />
        ) : (
          <>
            <Autocomplete
              options={vendors}
              getOptionLabel={(option) => option.legalVendorName || ""}
              isOptionEqualToValue={(o, v) => o?.id != null && v?.id != null && String(o.id) === String(v.id)}
              value={vendor}
              onChange={(_, newVal) => setVendor(newVal)}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{option.legalVendorName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.vendorGST} · {option.vendorState}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Vendor" required placeholder="Select a registered vendor" sx={fieldSx} />
              )}
            />
            <Typography variant="caption" sx={{ color: catalogTokens.color.textSecondary, display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
              <AddCircleOutlineIcon sx={{ fontSize: 14 }} />
              Vendor not listed?{" "}
              <Link component={RouterLink} to="/client-vendor-management" sx={{ fontWeight: 600 }}>
                Register a new vendor
              </Link>
            </Typography>
          </>
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {entries.map((entry, index) => (
          <CatalogEntryBlock
            key={entry.key}
            entry={entry}
            index={index}
            total={entries.length}
            onChange={updateEntry}
            onRemove={isReplaceMode ? null : removeEntry}
            brands={brands}
            categories={categories}
            subcategoryOptions={subcategoryOptionsFor(entry)}
            collections={collections}
            collectionGroupLabel={collectionGroupLabel}
            errorMessage={entry.submitError}
          />
        ))}
      </Box>

      {!isReplaceMode && (
        <Button
          onClick={addEntry}
          startIcon={<AddCircleOutlineIcon />}
          disabled={!isEntryValid(entries[entries.length - 1])}
          sx={{
            mt: 1.5,
            textTransform: "none",
            fontWeight: 700,
            color: catalogTokens.color.primary,
          }}
        >
          Add another catalog
        </Button>
      )}

      {uploading && <LinearProgress sx={{ borderRadius: 1, mt: 2 }} />}

      <Button
        variant="contained" disableElevation fullWidth
        onClick={handleSubmit}
        disabled={uploading || !canSubmit}
        sx={{
          mt: 2,
          borderRadius: `${catalogTokens.radius.md}px`,
          textTransform: "none",
          fontWeight: 700,
          py: 1.25,
          bgcolor: catalogTokens.color.primary,
          "&:hover": { bgcolor: catalogTokens.color.primaryDark },
        }}
      >
        {uploading
          ? isReplaceMode ? "Replacing…" : "Uploading…"
          : isReplaceMode
          ? "Replace Catalog"
          : entries.length > 1
          ? `Upload ${entries.length} Catalogs`
          : "Upload Catalog"}
      </Button>
    </Box>
  );
};

export default VendorCatalogUpload;
